import React, { useState, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../hooks/useAuth'
import { signIn } from '../lib/supabaseClient'

export default function LoginPage() {
  const { user, isDealer, isAdmin, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [showForgot, setShowForgot] = useState(false)

  // Redirect if already authenticated
  React.useEffect(() => {
    if (!authLoading && user) {
      const from = location.state?.from?.pathname
      if (from) {
        navigate(from, { replace: true })
      } else if (isAdmin) {
        navigate('/admin', { replace: true })
      } else if (isDealer) {
        navigate('/dashboard', { replace: true })
      } else {
        navigate('/', { replace: true })
      }
    }
  }, [user, isDealer, isAdmin, authLoading, navigate, location])

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault()
      setError(null)
      setSubmitting(true)
      try {
        const { data, error: signInError } = await signIn(email.trim(), password)
        if (signInError) {
          setError(signInError.message === 'Invalid login credentials'
            ? 'Неверный email или пароль'
            : signInError.message)
          setSubmitting(false)
          return
        }
        if (!data?.user) {
          setError('Не удалось войти')
          setSubmitting(false)
          return
        }
        // IMMEDIATE REDIRECT after successful login — no waiting for auth state
        setSubmitting(false)
        const userRole = data.user.role
        if (userRole === 'admin') {
          navigate('/admin', { replace: true })
        } else if (userRole === 'dealer') {
          navigate('/dashboard', { replace: true })
        } else {
          navigate('/', { replace: true })
        }
      } catch (err) {
        setError('Ошибка входа. Попробуйте позже.')
        setSubmitting(false)
      }
    },
    [email, password, navigate]
  )

  if (authLoading) {
    return (
      <div className="min-h-screen bg-ss-white flex items-center justify-center pt-16">
        <div className="w-8 h-8 border-2 border-ss-border border-t-ss-dark rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-ss-white flex items-center justify-center px-4 pt-16">
      <Helmet>
        <title>Вход для дилеров — SwedenSound</title>
        <meta name="description" content="Вход в личный кабинет дилера SwedenSound" />
      </Helmet>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-ss-text mb-2">SwedenSound</h1>
          <h2 className="text-lg text-ss-dark">Вход для дилеров</h2>
          <p className="text-sm text-ss-muted mt-1">Личный кабинет партнёров SwedenSound</p>
        </div>

        <div className="bg-white border border-ss-border rounded-2xl p-8 shadow-sm">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-ss-text mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="dealer@company.ru"
                required
                autoComplete="email"
                className="w-full px-4 py-3 bg-ss-gray border border-ss-border rounded-xl text-ss-text
                         placeholder:text-ss-muted focus:outline-none focus:border-ss-dark focus:ring-1 focus:ring-ss-dark
                         transition-all duration-200"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-ss-text mb-2">
                Пароль
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="w-full px-4 py-3 bg-ss-gray border border-ss-border rounded-xl text-ss-text
                         placeholder:text-ss-muted focus:outline-none focus:border-ss-dark focus:ring-1 focus:ring-ss-dark
                         transition-all duration-200"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-ss-dark text-white rounded-xl font-medium
                       hover:bg-ss-text active:scale-[0.98]
                       disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100
                       transition-all duration-200 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Вход...</span>
                </>
              ) : (
                'Войти'
              )}
            </button>
          </form>

          <div className="mt-5 text-center">
            <button
              onClick={() => setShowForgot(!showForgot)}
              className="text-sm text-ss-muted hover:text-ss-dark transition-colors"
            >
              Забыли пароль?
            </button>
          </div>

          {showForgot && (
            <div className="mt-4 p-4 bg-ss-gray rounded-xl text-sm text-ss-muted">
              Обратитесь к администратору для сброса пароля:
              <br />
              <a href="mailto:admin@sweden-sound.ru" className="text-ss-dark hover:underline">
                admin@sweden-sound.ru
              </a>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-ss-muted">
            Хотите стать дилером?{' '}
            <Link to="/contacts" className="text-ss-dark hover:underline font-medium">
              Оставьте заявку
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
